import { isValidIRDNumber } from "@fnzc/nz-ird-validator";
import { Box, Button, Checkbox as MuiCheckbox, FormControlLabel, Grid, useMediaQuery, useTheme } from "@material-ui/core";
import { differenceInYears } from "date-fns";
import { useEffect, useState } from "react";
import { RouteComponentProps } from "react-router-dom";
import * as yup from "yup";
import {
  AdviceDelivery,
  AdviceStatuses,
  AdviceType,
  AmlStatuses,
  clientPersonalInfoSchema,
  InvestmentOptions,
  KiwiSaverApplicationDto,
  kiwiSaverApplicationSchema,
  OnboardDelivery,
  Product,
  validDriverLicenceSchema,
  validPassportSchema,
} from "../../api";
import { Client, ScreenProps, SendApplicationToClientResult, SendMethod, titles, yesNoOptions } from "../../common/types";
import { useAppDispatch, useAppSelector } from "../../store";
import { clearClient, saveClient, setMessage, upsertClientAsync } from "../../store/adviser";
import { finaliseAsync, updateAsync } from "../../store/clientApplication";
import ConfirmSave from "../adviser/ConfirmSave";
import { getRoles, hasSelectedProductRoles } from "../adviser/Helpers";
import FormikWrapper from "../containers/FormikWrapper";
import { AlertMessage, Colors, FormLabel, SectionTitleText, SubtitleText, TitleText } from "../controls/helpers";
import { AddressAutocomplete, DateTextInput, InvestmentOption, NumberTextInput, RadioList, Select, TextInput } from "../controls/inputs";
import CalculateTaxRateDialog from "./CalculateTaxRateDialog";
import DownloadPds from "./DownloadPds";
import SelectIdentification, { getIdentificationOption, identificationOptions, identificationSchema } from "./SelectIdentification";
import SendApplicationToClient from "./SendApplicationToClient";

enum FormAttribute {
  ManualAddress,
  ManualPostalAddress,
  PostalAddressDifferent,
  RetentionAdmin,
}

enum FormMode {
  None,
  Confirm,
  HelpMeCalculate,
  Save,
  Send,
  ConfirmSave,
}

interface KsClient extends Client {
  sendMethod?: SendMethod;
  ksTotalAllocated?: string;
}

const KiwiSaverOnly = ({ history, inBrowserApp, match, nextRoute, route, prevRoute }: RouteComponentProps & ScreenProps) => {
  const dispatch = useAppDispatch();
  const { user, isLoadingUser } = useAppSelector((state) => state.oidc);
  const isMobileView = useMediaQuery(useTheme().breakpoints.down("sm"));
  const { client: adviserClient, upsertClientResult, advisers } = useAppSelector((state) => state.adviser);
  const { client } = useAppSelector((state) => state.clientApplication);
  const clientId = (match.params as { clientId: string }).clientId;
  const _client: Client | undefined = adviserClient || client;
  const [formMode, setFormMode] = useState<FormMode>(FormMode.None);
  const [formAttributes, setFormAttributes] = useState<FormAttribute[]>([...(!!_client?.postalAddress ? [FormAttribute.PostalAddressDifferent] : [])]);
  const isRetention = !!_client?.kiwiSaverApplication?.isRetention;
  const skipIdentification = isRetention || (inBrowserApp && _client?.amlStatus?.includes(AmlStatuses.LiveVerificationSkipped) && _client?.amlStatus?.includes(AmlStatuses.VerificationSkipped));
  const { passportAndDriverLicence, passport, driverLicence } = identificationOptions(inBrowserApp);

  useEffect(() => {
    if (!inBrowserApp && !isLoadingUser && user) {
      const roles = getRoles(user);
      roles.includes("Retention Admin") && !formAttributes.includes(FormAttribute.RetentionAdmin) && setFormAttributes((a) => [...a, FormAttribute.RetentionAdmin]);
    }
  }, [user]);

  const toggleFormAttribute = (attribute: FormAttribute) => {
    setFormAttributes(formAttributes.includes(attribute) ? formAttributes.filter((a) => a !== attribute) : [...formAttributes, attribute]);
  };

  const setManualResidentialAddress = (values: KsClient, setValues: (values: KsClient) => void) => {
    const isManualAddress = !formAttributes.includes(FormAttribute.ManualAddress);
    setValues({ ...values, residentialAddress: isManualAddress ? { streetAddress: "", suburb: "", city: "", postCode: "", country: "" } : undefined, isManualAddress });
    toggleFormAttribute(FormAttribute.ManualAddress);
  };

  const setManualPostalAddress = (values: KsClient, setValues: (values: KsClient) => void) => {
    const isManualPostalAddress = !formAttributes.includes(FormAttribute.ManualPostalAddress);
    setValues({ ...values, postalAddress: isManualPostalAddress ? { streetAddress: "", suburb: "", city: "", postCode: "", country: "" } : undefined, isManualPostalAddress });
    toggleFormAttribute(FormAttribute.ManualPostalAddress);
  };

  const clientSchema = () =>
    clientPersonalInfoSchema
      .concat(
        yup.object({
          mobile: yup.string(),
          pieTaxRate: yup.number().required("PIR is required"),
          kiwiSaverApplication: kiwiSaverApplicationSchema.concat(allocationSchema("ksTotalAllocated")),
          pdsDownloaded: yup.boolean().oneOf([true], "Please confirm that you have read the Product Disclosure Statement and that the information entered is correct"),
        })
      )
      .concat(identificationSchema(skipIdentification, inBrowserApp))
      .omit(["mobile", "countryOfBirth", "citizenships", "occupation"]);

  const initialValues = (resetForm?: boolean | undefined): KsClient => {
    const values = resetForm ? { products: [Product.KiwiSaver] } : _client;
    const identificationOption = getIdentificationOption(skipIdentification, inBrowserApp, values);

    return {
      firstName: "",
      middleName: "",
      lastName: "",
      mobile: "",
      email: "",
      dateOfBirth: undefined,
      irdNumber: "",
      salutation: "",
      occupation: "",
      residentialAddress: undefined,
      postalAddressDifferent: !!values?.postalAddress,
      postalAddress: undefined,
      pieTaxRate: undefined,
      onboardDelivery: OnboardDelivery.BrowserKiwiSaverOnly,
      adviceDelivery: AdviceDelivery.None,
      adviceStatus: AdviceStatuses.Skipped,
      adviceType: undefined,
      sendEmail: values?.adviceDelivery !== AdviceDelivery.InPerson,
      sendSms: false,
      useNZFundsDisclosureTemplate: false,
      ...values,
      smsMessage: values?.smsMessage || "",
      kiwiSaverApplication: {
        accountKey: undefined,
        investmentOption: undefined,
        isEligible: undefined,
        isRetention: undefined,
        isWithAnotherProvider: undefined,
        ...values?.kiwiSaverApplication,
        active: values?.adviceDelivery === AdviceDelivery.InPerson || values?.kiwiSaverApplication?.active,
      },
      pdsDownloaded: false,
      saveAsDraft: false,
      ksTotalAllocated: undefined,
      identificationOption,
      rego: !skipIdentification && !!values?.rego ? { numberPlate: "", ...values.rego } : undefined,
      passport: !skipIdentification && !!values?.passport ? { passportNumber: "", expiryDate: undefined, ...values.passport } : undefined,
      driverLicence: !skipIdentification && !!values?.driverLicence ? { licenceNumber: "", versionNumber: "", ...values.driverLicence } : undefined,
      sendMethod: values?.sendEmail && values?.sendSms ? SendMethod.EmailAndSms : values?.sendSms && !values?.sendEmail ? SendMethod.Sms : SendMethod.Email,
      sendToAdviser: false,
    };
  };

  const cleanValues = (values: KsClient) => ({
    ...values,
    postalAddress: !formAttributes.includes(FormAttribute.PostalAddressDifferent) ? undefined : values.postalAddress,
    passport: values.identificationOption === passport || values.identificationOption === passportAndDriverLicence ? { passportNumber: "", expiryDate: undefined, ...values.passport } : undefined,
    driverLicence:
      values.identificationOption === driverLicence || values.identificationOption === passportAndDriverLicence ? { licenceNumber: "", versionNumber: "", ...values.driverLicence } : undefined,
  });

  const sendApplicationToClient = (result: SendApplicationToClientResult, values?: KsClient) => {
    dispatch(upsertClientAsync(cleanValues({ ...(values || _client), ...result })));
    setFormMode(FormMode.Send);
  };

  const handleSubmit = (values: KsClient) => {
    const _values = cleanValues(values);

    if (!inBrowserApp) {
      dispatch(saveClient(_values));

      if (_values.sendToAdviser || _values.saveAsDraft) {
        if (isRetention || hasSelectedProductRoles(user, values?.products) || advisers?.length === 1) {
          dispatch(upsertClientAsync(_values));
          setFormMode(_values.saveAsDraft && !_values.sendToAdviser ? FormMode.Save : FormMode.Send);
        } else setFormMode(FormMode.ConfirmSave);
      } else {
        setFormMode(FormMode.Confirm);
      }
    } else {
      dispatch(updateAsync({ token: clientId, client: { ..._values, isNZTaxResident: true } })).then((result: any) => {
        if (!result.error) {
          dispatch(finaliseAsync({ token: clientId })).then((result: any) => {
            if (!result.error && !!nextRoute) nextRoute({ ..._values });
          });
        }
      });
    }
  };

  useEffect(() => {
    if (formMode === FormMode.Send) {
      if (upsertClientResult?.clientId) {
        dispatch(setMessage({ text: "The application has been sent successfully." }));
        setFormMode(FormMode.None);

        if (_client?.adviceDelivery === AdviceDelivery.InPerson) {
          dispatch(clearClient());
          history.replace(`/adviser/${clientId}/client-application`);
        } else {
          dispatch(clearClient());
        }
      }
    } else if (formMode === FormMode.None || formMode === FormMode.Save || formMode === FormMode.ConfirmSave) {
      if (upsertClientResult?.clientId) {
        dispatch(setMessage({ text: `The application has been ${_client?.saveAsDraft ? "saved" : "sent"} successfully.` }));
        setFormMode(FormMode.None);
        dispatch(clearClient());
      }
    }
  }, [_client, upsertClientResult, formMode]);

  return (
    <>
      <FormikWrapper initialValues={initialValues()} validationSchema={!inBrowserApp ? adviserSchema : clientSchema()} onSubmit={handleSubmit}>
        {({ resetForm, setFieldValue, setValues, values }) => {
          useEffect(() => {
            if (!_client && !inBrowserApp) {
              resetForm({ values: initialValues(true) });
              window.scrollTo(0, 0);
            }
          }, [_client]);

          return (
            <>
              {!inBrowserApp && route !== "kiwisaver-retention-tool" && (
                <Box>
                  {_client?.adviceDelivery !== AdviceDelivery.InPerson && <TitleText hideMarginBottom>KiwiSaver</TitleText>}
                  <SubtitleText hideMarginBottom>
                    <Grid container justifyContent="space-between">
                      <Grid item style={{ paddingTop: 3 }}>
                        {!!values.adviceType && (
                          <>
                            <span style={{ color: Colors.Black }}>
                              {values.adviceType === AdviceType.Comprehensive && "Comprehensive advice"}
                              {values.adviceType === AdviceType.Limited && "Limited advice"}
                              {values.adviceType === AdviceType.Information && "Information only"}
                            </span>
                            &emsp;
                          </>
                        )}
                        {values.adviceStatus === AdviceStatuses.Skipped ? "Product onboarding" : "Savings Advice Tool"}
                      </Grid>
                    </Grid>
                  </SubtitleText>
                </Box>
              )}
              <Box mb={3}>
                <SectionTitleText hideMarginTop={inBrowserApp}>{inBrowserApp ? "Personal information" : "Client"}</SectionTitleText>
                <Grid container spacing={3}>
                  <Grid item xs={12}>
                    <SubtitleText hideMarginBottom>
                      Enter {inBrowserApp ? "your" : "client"} name as shown on {inBrowserApp && "your "}NZ Driver Licence or NZ Passport.
                    </SubtitleText>
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <Select label="Title" name="salutation" options={titles} />
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextInput
                      placeholder={`First name as per ${inBrowserApp ? "your" : `client's`} ID`}
                      label={<>First name{!inBrowserApp && <span style={{ color: Colors.Red }}>*</span>}</>}
                      name="firstName"
                    />
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextInput placeholder={`Middle name as per ${inBrowserApp ? "your" : `client's`} ID`} label="Middle name(s)" name="middleName" />
                  </Grid>
                  <Grid item md={3} sm={6} xs={12}>
                    <TextInput
                      placeholder={`Last name as per ${inBrowserApp ? "your" : `client's`} ID`}
                      label={<>Last name{!inBrowserApp && <span style={{ color: Colors.Red }}>*</span>}</>}
                      name="lastName"
                    />
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <TextInput
                      label={<>Email{!inBrowserApp && <span style={{ color: Colors.Red }}>*</span>}</>}
                      name="email"
                      disabled={inBrowserApp}
                      autoComplete={inBrowserApp ? "username" : undefined}
                    />
                  </Grid>
                  <Grid item md={isRetention ? 3 : 6} sm={6} xs={12}>
                    <NumberTextInput
                      setStringValue
                      label="Mobile"
                      name="mobile"
                      mask="999999999999"
                      onBlur={(event) => {
                        if (!inBrowserApp && !event.target.value) {
                          setFieldValue("sendSms", false);
                          setFieldValue("sendMethod", SendMethod.Email);
                        }
                      }}
                    />
                  </Grid>
                  <Grid item md={isRetention ? 3 : 6} sm={6} xs={12}>
                    <DateTextInput label="Date of birth" name="dateOfBirth" />
                  </Grid>
                  {!isRetention && (
                    <Grid item sm={6} xs={12}>
                      <TextInput label="Occupation" name="occupation" />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    {!formAttributes.includes(FormAttribute.ManualAddress) ? (
                      <AddressAutocomplete label="Residential address (not PO Box)" name="residentialAddress" />
                    ) : (
                      <Grid container spacing={3}>
                        <Grid item sm={6} xs={12}>
                          <TextInput label="Street address" name="residentialAddress.streetAddress" />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <TextInput label="Suburb" name="residentialAddress.suburb" />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <TextInput label="City/Town" name="residentialAddress.city" />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <TextInput label="Postcode" name="residentialAddress.postCode" />
                        </Grid>
                        <Grid item sm={6} xs={12}>
                          <TextInput label="Country" name="residentialAddress.country" />
                        </Grid>
                      </Grid>
                    )}
                  </Grid>
                  {(!values.residentialAddress || formAttributes.includes(FormAttribute.ManualAddress)) && (
                    <Grid item xs={12}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setManualResidentialAddress(values, setValues);
                          return false;
                        }}
                      >
                        {!formAttributes.includes(FormAttribute.ManualAddress) ? `Can't find ${inBrowserApp ? "your" : "their"} address?` : `Find  ${inBrowserApp ? "your" : "their"} address`}
                      </a>
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <MuiCheckbox
                          color="primary"
                          checked={formAttributes.includes(FormAttribute.PostalAddressDifferent)}
                          onChange={() => {
                            toggleFormAttribute(FormAttribute.PostalAddressDifferent);
                            setValues({ ...values, postalAddressDifferent: !formAttributes.includes(FormAttribute.PostalAddressDifferent) });
                          }}
                        />
                      }
                      label={
                        <SubtitleText black hideMarginBottom>
                          Postal address is different from residential address.
                        </SubtitleText>
                      }
                      labelPlacement="end"
                    />
                  </Grid>
                  {formAttributes.includes(FormAttribute.PostalAddressDifferent) && (
                    <>
                      <Grid item xs={12}>
                        {!formAttributes.includes(FormAttribute.ManualPostalAddress) ? (
                          <AddressAutocomplete label="Postal address" name="postalAddress" />
                        ) : (
                          <Grid container spacing={3}>
                            <Grid item sm={6} xs={12}>
                              <TextInput label="Street address" name="postalAddress.streetAddress" />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                              <TextInput label="Suburb" name="postalAddress.suburb" />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                              <TextInput label="City/Town" name="postalAddress.city" />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                              <TextInput label="Postcode" name="postalAddress.postCode" />
                            </Grid>
                            <Grid item sm={6} xs={12}>
                              <TextInput label="Country" name="postalAddress.country" />
                            </Grid>
                          </Grid>
                        )}
                      </Grid>
                      {(!values.postalAddress || formAttributes.includes(FormAttribute.ManualPostalAddress)) && (
                        <Grid item xs={12}>
                          <a
                            href="#"
                            onClick={(e) => {
                              e.preventDefault();
                              setManualPostalAddress(values, setValues);
                              return false;
                            }}
                          >
                            {!formAttributes.includes(FormAttribute.ManualPostalAddress)
                              ? `Can't find ${inBrowserApp ? "your" : "their"} postal address?`
                              : `Find  ${inBrowserApp ? "your" : "their"} postal address`}
                          </a>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </Box>
              <Box mb={3}>
                <SectionTitleText>Tax</SectionTitleText>
                <Grid container spacing={3}>
                  <Grid item md={6} sm={12} xs={12}>
                    <Select
                      name="pieTaxRate"
                      label="Prescribed Investor Rate (PIR)"
                      options={[
                        { label: "10.5%", value: 0.105 },
                        { label: "17.5%", value: 0.175 },
                        { label: "28%", value: 0.28 },
                      ]}
                    />
                  </Grid>
                  {!isMobileView && (
                    <Grid item sm={6} xs={12}>
                      <NumberTextInput label="IRD number" name="irdNumber" mask="999999999" setStringValue />
                    </Grid>
                  )}
                  {inBrowserApp && (
                    <Grid item xs={12}>
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          setFormMode(FormMode.HelpMeCalculate);
                          return false;
                        }}
                      >
                        Help me calculate
                      </a>
                    </Grid>
                  )}
                  <CalculateTaxRateDialog ok={() => setFormMode(FormMode.None)} open={formMode === FormMode.HelpMeCalculate} />
                  {isMobileView && (
                    <Grid item sm={6} xs={12}>
                      <NumberTextInput label="IRD number" name="irdNumber" mask="999999999" setStringValue />
                    </Grid>
                  )}
                </Grid>
              </Box>
              <Box mb={3}>
                <SectionTitleText>KiwiSaver</SectionTitleText>
                <Grid container spacing={3}>
                  {!isRetention && (
                    <Grid item md={6} sm={12} xs={12}>
                      <RadioList
                        horizontal
                        name="kiwiSaverApplication.isWithAnotherProvider"
                        label={`${inBrowserApp ? "Are you" : "Is the client"} already in KiwiSaver?`}
                        options={yesNoOptions}
                        onChange={(isWithAnotherProvider) => isWithAnotherProvider && setFieldValue("kiwiSaverApplication.isEligible", undefined)}
                      />
                    </Grid>
                  )}
                  {!isRetention && values.kiwiSaverApplication?.isWithAnotherProvider === false && (
                    <Grid item md={6} sm={12} xs={12}>
                      <RadioList
                        horizontal
                        name="kiwiSaverApplication.isEligible"
                        label={`${inBrowserApp ? "Are you" : "Is the client"} eligible to join KiwiSaver?`}
                        options={yesNoOptions}
                        onChange={(isEligible) => {
                          if (inBrowserApp) setFieldValue("kiwiSaverApplication.active", isEligible);
                        }}
                        tooltip="You can join KiwiSaver if you are a New Zealand citizen, or entitled to live in New Zealand indefinitely, and you live or normally live in New Zealand. You cannot join KiwiSaver if you have a temporary, visitor, work or student visa."
                      />
                    </Grid>
                  )}
                  {values.kiwiSaverApplication?.isEligible === false && (
                    <Grid item xs={12}>
                      <AlertMessage error hideClose message={`${inBrowserApp ? "You are" : "The client is"} not eligible to apply for KiwiSaver. You will not be able to proceed.`} />
                    </Grid>
                  )}
                  {values.kiwiSaverApplication?.isEligible !== false && (inBrowserApp || _client?.adviceStatus === AdviceStatuses.Skipped) && (
                    <Grid item xs={12}>
                      <FormLabel label="Investment options" />
                      <InvestmentOption horizontal namePrefix="kiwiSaverApplication" kiwiSaver value={values.kiwiSaverApplication} showBalancedOption validationKey="ksTotalAllocated" />
                    </Grid>
                  )}
                </Grid>
              </Box>
              {!skipIdentification && <SelectIdentification inBrowserApp={inBrowserApp} />}
              {inBrowserApp && (values.kiwiSaverApplication?.isWithAnotherProvider || (!values.kiwiSaverApplication?.isWithAnotherProvider && values.kiwiSaverApplication?.isEligible)) && (
                <Box mb={3}>
                  <SectionTitleText>Document received</SectionTitleText>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <DownloadPds />
                    </Grid>
                  </Grid>
                </Box>
              )}
              <Grid container spacing={2} justifyContent="space-between">
                <Grid item>
                  {!!prevRoute && !inBrowserApp && (_client?.adviceDelivery !== AdviceDelivery.InPerson || _client?.productOptions?.length !== 1) && (
                    <Button
                      variant="contained"
                      type="button"
                      disabled={formMode !== FormMode.None}
                      onClick={() => {
                        dispatch(saveClient(values));
                        prevRoute(_client);
                      }}
                    >
                      Previous
                    </Button>
                  )}
                  {!!prevRoute && inBrowserApp && _client?.productOptions?.length !== 1 && (
                    <Button variant="contained" type="button" onClick={() => prevRoute(_client)}>
                      Previous
                    </Button>
                  )}
                </Grid>
                <Grid item>
                  <Grid container spacing={2}>
                    {!inBrowserApp && _client?.adviceDelivery !== AdviceDelivery.InPerson && (
                      <>
                        <Grid item>
                          <Button
                            variant="contained"
                            type="submit"
                            onClick={() => {
                              setFieldValue("saveAsDraft", true);
                              setFormMode(FormMode.Save);
                            }}
                          >
                            Save
                          </Button>
                        </Grid>
                        {isRetention && formAttributes.includes(FormAttribute.RetentionAdmin) && (
                          <Grid item>
                            <Button variant="contained" type="submit" fullWidth onClick={() => setValues({ ...values, sendToAdviser: true, saveAsDraft: true })}>
                              Send to adviser
                            </Button>
                          </Grid>
                        )}
                      </>
                    )}
                    {(inBrowserApp || isRetention || hasSelectedProductRoles(user, values?.products)) && (
                      <Grid item>
                        <Button
                          variant="contained"
                          color="primary"
                          type="submit"
                          disabled={inBrowserApp ? !values.kiwiSaverApplication?.isWithAnotherProvider && values.kiwiSaverApplication?.isEligible === false : formMode !== FormMode.None}
                          onClick={() => setValues({ ...values, sendToAdviser: false, saveAsDraft: false })}
                        >
                          {inBrowserApp ? "Submit" : "Send to client"}
                        </Button>
                      </Grid>
                    )}
                  </Grid>
                </Grid>
              </Grid>
              {!isRetention && !hasSelectedProductRoles(user, values?.products) && <ConfirmSave client={_client!} open={formMode === FormMode.ConfirmSave} cancel={() => setFormMode(FormMode.None)} />}
            </>
          );
        }}
      </FormikWrapper>
      {!inBrowserApp && (
        <SendApplicationToClient
          hasMobile={!!_client?.mobile}
          open={formMode === FormMode.Confirm}
          cancel={() => setFormMode(FormMode.None)}
          ok={sendApplicationToClient}
          isRetention={isRetention}
          isRetentionAdmin={formAttributes.includes(FormAttribute.RetentionAdmin)}
        />
      )}
    </>
  );
};

const adviserSchema = yup.object({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  email: yup.string().email("Email is invalid").required("Email is required"),
  irdNumber: yup.string().test("IRD valid", "IRD number is invalid", (value) => !value || isValidIRDNumber(value)),
  sendMethod: yup.mixed<SendMethod>().required("An invitation method is required"),
  passport: validPassportSchema,
  driverLicence: validDriverLicenceSchema,
  dateOfBirth: yup
    .date()
    .test("DOB valid", "Must be 100 years or less", (value) => !value || differenceInYears(new Date(), value || new Date()) <= 100)
    .when("kiwiSaverApplication", {
      is: (val?: KiwiSaverApplicationDto) => !!val?.active,
      then: yup.date().test("DOB valid", "Must be 18 years or over", (value) => !value || differenceInYears(new Date(), value || new Date()) >= 18),
    })
    .when("kiwiSaverApplication", {
      is: (val?: KiwiSaverApplicationDto) => !val?.active,
      then: yup.date().test("DOB valid", "Must be 16 years or over", (value) => !value || differenceInYears(new Date(), value || new Date()) >= 16),
    }),
});

const allocationSchema = (validationKey: string) =>
  yup.object().test("100% allocated", "Total allocation must be 100%", function (values: any) {
    if (values.investmentOption !== InvestmentOptions.SelfSelect) return true;
    else
      return (
        (Number(values["income"]) || 0) + (Number(values["inflation"]) || 0) + (Number(values["growth"]) || 0) === 100 ||
        this?.createError({ path: validationKey, message: "Total allocation must be 100%" })
      );
  });

export default KiwiSaverOnly;
